import { Company } from "@/types/company";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { MoreHorizontal, UserPlus, Trash2, UserCog } from "lucide-react";
import { MapPin } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useState } from "react";
import { LicenseUpdateDialog } from "./LicenseUpdateDialog";
import { DeleteCompanyDialog } from "./DeleteCompanyDialog";
import { BulkEmailDialog } from "./BulkEmailDialog";
import { BulkLicenseDialog } from "./BulkLicenseDialog";
import { CompanyTableToolbar } from "./CompanyTableToolbar";
import { Checkbox } from "@/components/ui/checkbox";
import { AddAdminModal } from "../admin/AddAdminModal";
import { FileX, Users, Calendar, Crown, Clock, Pencil, ToggleLeft, ChevronLeft, ChevronRight } from "lucide-react";

interface CompanyTableProps {
  companies: Company[];
  onEdit: (company: Company) => void;
  onDelete: (companyId: string) => void;
  onUpdateLicense: (companyId: string, isActive: boolean) => void;
  onExportExcel: (companies: Company[]) => void;
  onExportPdf: (companies: Company[]) => void;
  onAddAdmin: (data: { companyId: string; name: string; email: string; password: string }) => void;
  onSwitchToManager?: (companyId: string) => void;
  selectedCompanies: Company[];
  onToggleCompany: (company: Company) => void;
  onToggleAll: (checked: boolean) => void;
  isSelected: (company: Company) => boolean;
  isAllSelected: boolean;
  clearSelection: () => void;
}

export function CompanyTable({
  companies,
  onEdit,
  onDelete,
  onUpdateLicense,
  onExportExcel,
  onExportPdf,
  onAddAdmin,
  onSwitchToManager,
  selectedCompanies,
  onToggleCompany,
  onToggleAll,
  isSelected,
  isAllSelected,
  clearSelection,
}: CompanyTableProps) {
  // Dialoglar için state
  const [licenseDialog, setLicenseDialog] = useState<{
    isOpen: boolean;
    company: Company | null;
    newState: boolean;
  }>({
    isOpen: false,
    company: null,
    newState: false,
  });

  const [deleteDialog, setDeleteDialog] = useState<{
    isOpen: boolean;
    company: Company | null;
  }>({
    isOpen: false,
    company: null,
  });

  const [bulkEmailDialog, setBulkEmailDialog] = useState(false);
  const [bulkLicenseDialog, setBulkLicenseDialog] = useState<{
    isOpen: boolean;
    isActivating: boolean;
  }>({
    isOpen: false,
    isActivating: false,
  });

  const [showAddAdminModal, setShowAddAdminModal] = useState(false);
  const [company, setCompany] = useState<Company | null>(null);

  // Sayfalama için state'ler
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Toplam sayfa sayısını hesapla
  const totalPages = Math.ceil(companies.length / itemsPerPage);

  // Mevcut sayfada gösterilecek firmaları belirle
  const currentCompanies = companies.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Sayfa değiştirme fonksiyonu
  const changePage = (page: number) => {
    setCurrentPage(page);
  };

  // Önceki ve sonraki sayfalara geçiş fonksiyonları
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <div className="space-y-4">
      {/* Toplu İşlem Toolbar */}
      <CompanyTableToolbar
        selectedCompanies={selectedCompanies}
        clearSelection={clearSelection}
        onSendEmail={() => setBulkEmailDialog(true)}
        onExportExcel={() => onExportExcel(selectedCompanies)}
        onExportPdf={() => onExportPdf(selectedCompanies)}
      />

      {/* Firma Tablosu */}
      <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
        <Table className="w-full table-fixed">
          <TableHeader>
            <TableRow className="bg-gradient-to-r from-slate-50 to-slate-100 hover:from-slate-50 hover:to-slate-100 border-b border-slate-200">
              <TableHead className="w-10 py-4 pl-6">
                <div className="flex items-center">
                  <Checkbox
                    checked={isAllSelected}
                    onChange={() => onToggleAll(true)}
                    aria-label="Tümünü seç"
                    className="rounded border-slate-300"
                  />
                </div>
              </TableHead>
              <TableHead className="py-4 text-slate-700 font-bold text-sm w-[280px]">Firma Bilgileri</TableHead>
              <TableHead className="py-4 text-slate-700 font-bold text-sm w-[200px]">Lisans Durumu</TableHead>
              <TableHead className="py-4 text-slate-700 font-bold text-sm w-[200px]">Yönetici</TableHead>
              <TableHead className="py-4 text-slate-700 font-bold text-sm w-[120px]">Konum</TableHead>
              <TableHead className="text-right py-4 pr-6 text-slate-700 font-bold text-sm w-[80px]">İşlemler</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {currentCompanies.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="h-24 text-center">
                  <div className="flex flex-col items-center justify-center text-slate-500">
                    <FileX className="h-10 w-10 mb-2 text-slate-400" />
                    <p>Firma bulunamadı.</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              currentCompanies.map((company) => (
                <TableRow key={company._id} className="border-b border-slate-100 hover:bg-blue-50/30 transition-colors">
                  <TableCell className="py-4 pl-6">
                    <div className="flex items-center">
                      <Checkbox
                        checked={isSelected(company)}
                        onChange={() => onToggleCompany(company)}
                        aria-label={`${company.name} firmasını seç`}
                        className="rounded border-slate-300"
                      />
                    </div>
                  </TableCell>
                  <TableCell className="py-4">
                    <div className="flex flex-col">
                      <span className="font-semibold text-slate-900 break-words text-base">
                        {company.name}
                      </span>
                      <span className="text-sm text-slate-500 mt-0.5">
                        {company.phone}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="py-4">
                    <div className="space-y-2.5">
                      <div className="flex flex-wrap gap-2">
                        {company.license.isActive ? (
                          <div className="inline-flex items-center rounded-lg bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-800">
                            <div className="mr-1.5 h-1.5 w-1.5 rounded-full bg-green-600"></div>
                            Aktif
                          </div>
                        ) : (
                          <div className="inline-flex items-center rounded-lg bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-800">
                            <div className="mr-1.5 h-1.5 w-1.5 rounded-full bg-red-600"></div>
                            Pasif
                          </div>
                        )}

                        {company.license.type === 'premium' ? (
                          <div className="inline-flex items-center rounded-lg bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800">
                            <Crown className="h-3 w-3 mr-1 text-amber-600" />
                            Premium
                          </div>
                        ) : (
                          <div className="inline-flex items-center rounded-lg bg-blue-100 px-2.5 py-1 text-xs font-semibold text-blue-800">
                            <Clock className="h-3 w-3 mr-1 text-blue-600" />
                            Trial
                          </div>
                        )}
                      </div>


                      <div className="flex items-center gap-3 text-xs">
                        <div className="flex items-center text-slate-600">
                          <Users className="h-3.5 w-3.5 mr-1 text-slate-400" />
                          <span className="font-medium">{company.license.count} Lisans</span>
                        </div>
                      </div>

                      {/* Lisans bitiş tarihi */}
                      <div className="flex items-center text-xs">
                        <Calendar className="h-3.5 w-3.5 mr-1.5 flex-shrink-0 text-slate-400" />
                        <span>
                          {new Date() > new Date(company.license.expiryDate)
                            ? (
                              <span className="text-red-600 font-semibold">
                                Süresi Dolmuş
                              </span>
                            )
                            : (() => {
                                const expiryDate = new Date(company.license.expiryDate);
                                const today = new Date();
                                const diffTime = expiryDate.getTime() - today.getTime();
                                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                                let remainingTime = "";
                                if (diffDays === 0) {
                                  remainingTime = "Bugün bitiyor";
                                } else if (diffDays === 1) {
                                  remainingTime = "Yarın bitiyor";
                                } else if (diffDays <= 30) {
                                  remainingTime = `${diffDays} gün kaldı`;
                                } else if (diffDays <= 365) {
                                  const months = Math.floor(diffDays / 30);
                                  remainingTime = `${months} ay kaldı`;
                                } else {
                                  const years = Math.floor(diffDays / 365);
                                  remainingTime = `${years} yıl kaldı`;
                                }

                                return (
                                  <span className={`${diffDays <= 30 ? 'text-orange-600 font-semibold' : 'text-slate-600 font-medium'}`}>
                                    {remainingTime}
                                  </span>
                                );
                              })()
                          }
                        </span>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="py-4">
                    {company.admins?.length > 0 ? (
                      <div className="space-y-2">
                        {company.admins.map((admin) => (
                          <div
                            key={admin._id}
                            className="flex items-center"
                          >
                            <div className="h-9 w-9 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-semibold shadow-sm mr-2.5">
                              {admin.name.charAt(0).toUpperCase()}
                            </div>
                            <div className="flex flex-col">
                              <span className="text-sm font-semibold text-slate-800">
                                {admin.name}
                              </span>
                              <span className="text-xs text-slate-500">
                                {admin.email}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex items-center text-slate-400 text-sm">
                        <UserPlus className="h-4 w-4 mr-2" />
                        <span>Yönetici ekle</span>
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="py-4">
                    <div className="flex items-center">
                      <div className="h-8 w-8 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500 mr-2.5">
                        <MapPin className="h-4 w-4" />
                      </div>
                      <span className="text-sm font-medium text-slate-700">
                        {company.city || "Belirtilmemiş"}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right pr-6">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-9 w-9 p-0 rounded-lg hover:bg-slate-100">
                          <span className="sr-only">İşlemler</span>
                          <MoreHorizontal className="h-5 w-5 text-slate-600" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56">
                        <DropdownMenuItem onClick={() => onEdit(company)}>
                          <Pencil className="h-4 w-4 mr-2 text-slate-500" />
                          <span>Düzenle</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            setLicenseDialog({
                              isOpen: true,
                              company,
                              newState: !company.license.isActive,
                            })
                          }
                        >
                          <ToggleLeft className="h-4 w-4 mr-2 text-slate-500" />
                          <span>Lisansı {company.license.isActive ? "Pasif" : "Aktif"} Yap</span>
                        </DropdownMenuItem>
                        {company.admins?.length > 0 && onSwitchToManager && (
                          <DropdownMenuItem
                            onClick={() => onSwitchToManager(company.admins[0]._id)}
                          >
                            <UserCog className="h-4 w-4 mr-2 text-slate-500" />
                            <span>Yönetici Olarak Devam Et</span>
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          onClick={() => {
                            setCompany(company);
                            setShowAddAdminModal(true);
                          }}
                        >
                          <UserPlus className="h-4 w-4 mr-2 text-slate-500" />
                          <span>Yönetici Ekle</span>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-red-600"
                          onClick={() =>
                            setDeleteDialog({ isOpen: true, company })
                          }
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          <span>Sil</span>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Sayfalama kontrolleri */}
      {companies.length > itemsPerPage && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <div className="text-sm text-slate-600">
            <span className="font-semibold text-slate-800">{companies.length}</span> firma içinden{" "}
            <span className="font-semibold text-slate-800">{(currentPage - 1) * itemsPerPage + 1}</span>-
            <span className="font-semibold text-slate-800">{Math.min(currentPage * itemsPerPage, companies.length)}</span>{" "}
            arası gösteriliyor
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
              className="h-10 px-4 rounded-lg border-slate-200 hover:bg-slate-50 disabled:opacity-50"
              aria-label="Önceki sayfa"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">Önceki</span>
            </Button>

            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                // Sayfa düğmeleri için başlangıç sayfasını hesapla
                let startPage = Math.max(1, currentPage - 2);
                if (currentPage > totalPages - 2) {
                  startPage = Math.max(1, totalPages - 4);
                }

                const page = startPage + i;
                if (page <= totalPages) {
                  return (
                    <Button
                      key={page}
                      variant={currentPage === page ? "default" : "outline"}
                      size="sm"
                      onClick={() => changePage(page)}
                      className={`h-10 min-w-10 px-3 rounded-lg ${
                        currentPage === page
                          ? "bg-blue-600 hover:bg-blue-700 text-white pointer-events-none"
                          : "border-slate-200 hover:bg-slate-50"
                      }`}
                    >
                      {page}
                    </Button>
                  );
                }
                return null;
              })}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
              className="h-10 px-4 rounded-lg border-slate-200 hover:bg-slate-50 disabled:opacity-50"
              aria-label="Sonraki sayfa"
            >
              <span className="hidden sm:inline">Sonraki</span>
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Lisans Güncelleme Dialog */}
      {licenseDialog.isOpen && licenseDialog.company && (
        <LicenseUpdateDialog
          isOpen={licenseDialog.isOpen}
          onClose={() => setLicenseDialog({ isOpen: false, company: null, newState: false })}
          onConfirm={() => {
            if (licenseDialog.company) {
              onUpdateLicense(licenseDialog.company._id, licenseDialog.newState);
            }
          }}
          companyName={licenseDialog.company.name}
          isActivating={licenseDialog.newState}
        />
      )}

      {/* Silme Dialog */}
      {deleteDialog.isOpen && deleteDialog.company && (
        <DeleteCompanyDialog
          isOpen={deleteDialog.isOpen}
          onClose={() => setDeleteDialog({ isOpen: false, company: null })}
          onConfirm={() => {
            if (deleteDialog.company) {
              onDelete(deleteDialog.company._id);
            }
          }}
          companyName={deleteDialog.company.name}
        />
      )}

      {/* Yönetici Ekleme Modal */}
      {showAddAdminModal && company && (
        <AddAdminModal
          company={company}
          onClose={() => setShowAddAdminModal(false)}
          onSubmit={async (data) => {
            try {
              await onAddAdmin({
                companyId: company._id,
                name: data.name,
                email: data.email,
                password: data.password
              });
              setShowAddAdminModal(false);
            } catch (error) {
              console.error('Yönetici eklenirken hata:', error);
            }
          }}
        />
      )}

      {/* Toplu İşlem Dialogları */}
      {bulkEmailDialog && (
        <BulkEmailDialog
          isOpen={bulkEmailDialog}
          onClose={() => setBulkEmailDialog(false)}
          companies={selectedCompanies.length > 0 ? selectedCompanies : companies}
        />
      )}

      {bulkLicenseDialog.isOpen && (
        <BulkLicenseDialog
          isOpen={bulkLicenseDialog.isOpen}
          onClose={() =>
            setBulkLicenseDialog({ isOpen: false, isActivating: false })
          }
          onConfirm={() => {
            selectedCompanies.forEach((company) => {
              onUpdateLicense(company._id, bulkLicenseDialog.isActivating);
            });
          }}
          companies={selectedCompanies}
          isActivating={bulkLicenseDialog.isActivating}
        />
      )}
    </div>
  );
}
